/**
 * Contacts — choosing who to talk to.
 *
 * Every "new chat" affordance in the app leads here, and until now it led
 * to a placeholder, so starting a conversation was impossible through the
 * interface. The real-time test had to reach past the UI and call
 * contacts.findByPhone directly for want of this screen.
 *
 * Structured after WhatsApp's new-chat screen: the action rows first —
 * New group, New contact, New community — then the contacts themselves
 * under a heading.
 *
 * ONE DELIBERATE DIFFERENCE. WhatsApp reads the device phone book, so its
 * list arrives populated and a number lookup is a secondary path. ZIvapp
 * does not read contacts and on the web cannot, so the lookup IS how
 * anyone gets onto the list — without it the list stays empty forever and
 * no conversation can ever be started. It therefore appears inline the
 * moment what you have typed could be dialled, rather than being buried
 * behind New contact.
 *
 * Selecting anyone opens the direct chat, creating it if this is the first
 * message. That is getOrCreateDirect's whole purpose.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { contacts as contactService, chats, toE164, isValidE164, userMessage } from '@zivapp/shared'
import { COUNTRIES_BY_NAME, countryByCode } from '@zivapp/shared/countries'
import { useAuth } from '../context/index.js'
import Avatar from '../components/ui/Avatar.jsx'
import './Contacts.css'

const DEFAULT_CODE = 'NG'

export default function Contacts () {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [saved, setSaved] = useState([])
  const [isLoading, setLoading] = useState(true)
  const [query, setQuery] = useState('')

  const [lookup, setLookup] = useState(null)     // the user a phone search found
  const [isSearching, setSearching] = useState(false)
  const [error, setError] = useState(null)
  const [opening, setOpening] = useState(null)   // id of the chat being opened

  useEffect(() => contactService.subscribe(rows => {
    setSaved(rows)
    setLoading(false)
  }), [])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return saved
    return saved.filter(c =>
      (c.savedName ?? '').toLowerCase().includes(q) ||
      (c.user?.displayName ?? '').toLowerCase().includes(q)
    )
  }, [saved, query])

  /**
   * A query that could be a phone number is worth looking up. Anything with
   * digits and a plausible length qualifies — being too eager here costs a
   * request, being too strict costs the only way to find anybody.
   */
  const asPhone = useMemo(() => {
    const raw = query.trim()
    if (!/^[+\d][\d\s()-]{6,}$/.test(raw)) return null
    try {
      const e164 = toE164(raw, countryByCode(DEFAULT_CODE)?.dial ?? '234')
      return isValidE164(e164) ? e164 : null
    } catch {
      return null
    }
  }, [query])

  async function findByPhone () {
    if (!asPhone) return
    setSearching(true)
    setError(null)
    setLookup(null)
    try {
      const found = await contactService.findByPhone(asPhone)
      if (found) setLookup(found)
      else setError(`No ZIvapp account for ${asPhone}.`)
    } catch (err) {
      setError(userMessage(err))
    } finally {
      setSearching(false)
    }
  }

  async function openChatWith (userId, savedName) {
    setOpening(userId)
    setError(null)
    try {
      // Saving first, so the conversation has a name to show rather than
      // appearing as an unknown number.
      if (savedName) {
        await contactService.add({ userId, savedName }).catch(() => {})
      }
      const chat = await chats.getOrCreateDirect(userId)
      navigate(`/chats/${chat.id}`)
    } catch (err) {
      setError(userMessage(err))
      setOpening(null)
    }
  }

  return (
    <div className="ct">
      <header className="ct__bar">
        <button className="ct__back" type="button" onClick={() => navigate('/chats')}
          aria-label="Back to chats">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="ct__heading">
          <h1>Select contact</h1>
          <p>{saved.length} {saved.length === 1 ? 'contact' : 'contacts'}</p>
        </div>
      </header>

      <div className="ct__search">
        <svg className="ct__search-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.9" />
          <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
        <input
          className="ct__search-input"
          value={query}
          onChange={e => { setQuery(e.target.value); setLookup(null); setError(null) }}
          onKeyDown={e => { if (e.key === 'Enter') findByPhone() }}
          placeholder="Search name or enter a phone number"
          aria-label="Search contacts or enter a phone number"
          autoFocus
        />
      </div>

      {/* The actions, as in the reference. Group creation has routes behind
          it; communities do not exist yet, and the row says so rather than
          leading somewhere empty. */}
      {!query && (
        <ul className="ct__actions">
          <li>
            <button className="ct__action" type="button" onClick={() => navigate('/groups/new')}>
              <span className="ct__action-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="9" cy="9" r="3.1" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M3.6 18.4v-1a3.6 3.6 0 0 1 3.6-3.6h3.6a3.6 3.6 0 0 1 3.6 3.6v1"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M16.6 5.4a3 3 0 0 1 0 5.6M17.6 14.2c2 .4 3.4 2 3.4 4v.2"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              New group
            </button>
          </li>

          {/*
            Message yourself.

            The place people keep links, numbers and notes they want on
            every device. The server used to refuse a chat with yourself
            outright; it is a direct chat with one participant now.
          */}
          <li>
            <button
              className="ct__action"
              type="button"
              data-testid="message-yourself"
              onClick={() => user && openChatWith(user.id)}
              disabled={!user || opening === user?.id}
            >
              <span className="ct__action-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8.2" r="3.6" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M4.8 19.4v-.8a5 5 0 0 1 5-5h4.4a5 5 0 0 1 5 5v.8"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              {user?.displayName ? `${user.displayName} (You)` : 'Message yourself'}
              <span className="ct__action-hint">Notes to self</span>
            </button>
          </li>

          <li>
            <button
              className="ct__action"
              type="button"
              data-testid="new-contact"
              onClick={() => navigate('/contacts/new')}
            >
              <span className="ct__action-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="8.6" r="3.6" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M3.8 19.4v-.8a5 5 0 0 1 5-5h4.4" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M18 14v6M15 17h6" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" />
                </svg>
              </span>
              New contact
              <span className="ct__action-hint">by phone number</span>
            </button>
          </li>

          <li>
            <button
              className="ct__action"
              type="button"
              onClick={() => navigate('/communities/new')}
            >
              <span className="ct__action-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="8.5" cy="8.6" r="3" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M3.4 19v-1.4a3.4 3.4 0 0 1 3.4-3.4h3.4a3.4 3.4 0 0 1 3.4 3.4V19"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M16 6.2a3 3 0 0 1 0 5.6M17 14.2c2 .4 3.4 2 3.4 4V19"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              New community
            </button>
          </li>
        </ul>
      )}


      {/* Offered only when what was typed could actually be dialled. */}
      {asPhone && (
        <button className="ct__find" type="button" onClick={findByPhone} disabled={isSearching}>
          <span className="ct__find-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" />
            </svg>
          </span>
          {isSearching ? 'Searching…' : `Find ${asPhone} on ZIvapp`}
        </button>
      )}

      {error && <p className="ct__error" role="alert">{error}</p>}

      {lookup && (
        <section className="ct__found">
          <h2>Found on ZIvapp</h2>
          <Row
            name={lookup.displayName || asPhone}
            about={lookup.about}
            avatarUrl={lookup.avatarUrl}
            busy={opening === lookup.id}
            onClick={() => openChatWith(lookup.id, lookup.displayName || asPhone)}
          />
        </section>
      )}

      <div className="ct__scroll">
        {isLoading ? (
          <p className="ct__state">Loading contacts…</p>
        ) : visible.length === 0 ? (
          <div className="ct__empty">
            <span className="ct__empty-art" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8.6" r="3.6" stroke="currentColor" strokeWidth="1.3" />
                <path d="M4.8 19.4v-.8a5 5 0 0 1 5-5h4.4a5 5 0 0 1 5 5v.8"
                  stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </span>
            <p className="ct__empty-title">
              {query ? 'No matching contacts' : 'No contacts yet'}
            </p>
            <p className="ct__empty-text">
              Enter a phone number above to find someone on ZIvapp.
            </p>
          </div>
        ) : (
          <>
            <h2 className="ct__section">Contacts on ZIvapp</h2>
            <ul className="ct__list">
            {visible.map(contact => (
              <li key={contact.id ?? contact.userId}>
                <Row
                  name={contact.savedName || contact.user?.displayName || 'Unknown'}
                  about={contact.user?.about}
                  avatarUrl={contact.user?.avatarUrl}
                  online={contact.user?.isOnline}
                  busy={opening === (contact.userId ?? contact.user?.id)}
                  onClick={() => openChatWith(contact.userId ?? contact.user?.id)}
                />
              </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

function Row ({ name, about, avatarUrl, online, busy, onClick }) {
  return (
    <button className="ct__row" type="button" onClick={onClick} disabled={busy}>
      <span className="ct__avatar">
        <Avatar name={name} url={avatarUrl} size={47} />
        {online && <span className="ct__online" aria-label="Online" />}
      </span>

      <span className="ct__body">
        <span className="ct__name">{name}</span>
        <span className="ct__about">{busy ? 'Opening…' : (about || 'Hey there! I am on ZIvapp.')}</span>
      </span>
    </button>
  )
}
